import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ListVideo, Lock, Trash2 } from 'lucide-react';
import { Playlist } from '../types/playlist';

interface PlaylistCardProps {
  playlist: Playlist;
  isOwner?: boolean;
  onClick?: () => void;
  onDelete?: (playlist: Playlist) => void;
  /** Owner only: switch between public and private. */
  onToggleVisibility?: (playlist: Playlist, isPublic: boolean) => void;
}

const PlaylistCard: React.FC<PlaylistCardProps> = ({ playlist, isOwner, onClick, onDelete, onToggleVisibility }) => {
  const { t } = useTranslation();

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick?.()}
      aria-label={t('playlist.open_aria', { title: playlist.title })}
      style={{
        background: 'var(--card-bg, var(--theme-card, #fff))',
        border: '1px solid var(--theme-border, #e5e7eb)',
        borderRadius: 10,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease, transform 0.1s ease',
        display: 'flex', flexDirection: 'column',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
        (e.currentTarget as HTMLDivElement).style.transform = 'none';
      }}
    >
      {/* Thumbnail / Placeholder */}
      <div
        style={{
          height: 100, position: 'relative',
          background: playlist.thumbnail_url
            ? `url(${playlist.thumbnail_url}) center/cover`
            : 'color-mix(in srgb, var(--theme-primary, #1976d2) 15%, var(--card-bg, #fff))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {!playlist.thumbnail_url && (
          <ListVideo size={32} style={{ color: 'var(--primary-text, #1976d2)', opacity: 0.7 }} aria-hidden />
        )}
        <span
          style={{
            position: 'absolute', bottom: 6, right: 8,
            background: 'rgba(0,0,0,0.65)', color: '#fff',
            fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
          }}
        >
          {t('channel.video_count', { count: playlist.videoIds.length })}
        </span>
      </div>

      {/* Info */}
      <div style={{ padding: '10px 12px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
          <h4
            style={{
              margin: 0, fontSize: 13, fontWeight: 600,
              color: 'var(--card-title-color, var(--text-primary, #111))',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
            }}
          >
            {playlist.title}
          </h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {!isOwner && !playlist.isPublic && (
              <Lock size={11} style={{ color: 'var(--text-secondary, #9ca3af)' }} aria-label={t('playlist.private_aria')} />
            )}
            {isOwner && onDelete && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(playlist); }}
                onKeyDown={(e) => e.stopPropagation()}
                aria-label={t('playlist.delete_aria')}
                title={t('playlist.delete_aria')}
                className="airec-icon-btn"
                style={{
                  width: 28, height: 28, margin: '-5px -6px -5px 0', borderRadius: 6,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-secondary, #6b7280)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Trash2 size={14} aria-hidden />
              </button>
            )}
          </div>
        </div>
        {playlist.description && (
          <p style={{
            margin: 0, fontSize: 11,
            color: 'var(--text-secondary, #6b7280)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {playlist.description}
          </p>
        )}
        {/* Owner: Public / Private chip — click to change it (asked to confirm); read-only shows it only. */}
        {isOwner && (() => {
          const Tag = onToggleVisibility ? 'button' : 'span';
          return (
            <Tag
              {...(onToggleVisibility && {
                type: 'button' as const,
                onClick: (e: React.MouseEvent) => { e.stopPropagation(); onToggleVisibility(playlist, !playlist.isPublic); },
                onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
                title: playlist.isPublic
                  ? t('playlist.make_private_hint', 'Visible to everyone who can see this channel. Click to make it private.')
                  : t('playlist.make_public_hint', 'Only you can see it. Click to make it public.'),
                'aria-label': playlist.isPublic ? t('playlist.make_private') : t('playlist.make_public'),
              })}
              className={onToggleVisibility ? 'airec-chip-btn' : undefined}
              style={{
                alignSelf: 'flex-start', marginTop: 4,
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '3px 10px', borderRadius: 12, cursor: onToggleVisibility ? 'pointer' : 'default',
                fontSize: 11, fontWeight: 600,
                border: '1px solid',
                borderColor: playlist.isPublic
                  ? 'color-mix(in srgb, var(--theme-primary, #1976d2) 35%, transparent)'
                  : 'var(--theme-border, #e5e7eb)',
                background: playlist.isPublic
                  ? 'color-mix(in srgb, var(--theme-primary, #1976d2) 10%, transparent)'
                  : 'var(--surface-hover, #f3f4f6)',
                color: playlist.isPublic ? 'var(--primary-text, var(--theme-primary, #1976d2))' : 'var(--text-secondary, #6b7280)',
              }}
            >
              {playlist.isPublic ? <Globe size={12} aria-hidden /> : <Lock size={12} aria-hidden />}
              {playlist.isPublic ? t('playlist.public_label', 'Public') : t('playlist.private_label', 'Private')}
            </Tag>
          );
        })()}
      </div>
    </div>
  );
};

export default PlaylistCard;
